<script setup lang="ts">
import { nextTick, ref, watch } from 'vue'
import anime from 'animejs'
import { XMarkIcon } from '@heroicons/vue/24/solid'
import { Toast, ToastAction } from '../types'

const DURATION = 800
const WIDTH_STEP = 50
const HEIGHT_SHORT = '10px'
const DURATION_LONG = DURATION * 1.2

interface Props {
    toasts?: Toast[]
    pause?: () => void
    resume?: () => void
    hide?: (id?: number) => void
}

const props = withDefaults(defineProps<Props>(), {
    toasts: () => [],
    pause: undefined,
    resume: undefined,
    hide: undefined,
})

const wrappers = ref()
const elements = ref()
const isHover = ref(false)

const elementClasses = (toast: Toast) => [
    'toast__element',
    {
        'toast__element--dark': toast.type === 'dark',
        'toast__element--primary': toast.type === 'primary',
        'toast__element--success': toast.type === 'success',
        'toast__element--danger': toast.type === 'danger',
    },
]

const animateToast = (el: HTMLDivElement | HTMLElement | HTMLDivElement[], options: any) => {
    anime({
        targets: el,
        easing: 'easeOutExpo',
        translateZ: 0,
        ...options,
    })
}

// Toasts can be updated after they appear: resize the expanded ones to their new content
watch(
    () => props.toasts.map((toast) => [toast.label, toast.description, toast.closable, toast.actions.length]),
    async () => {
        await nextTick()
        ;((elements.value ?? []) as HTMLDivElement[]).forEach((el) => {
            const inner = el.querySelector('.toast__inner') as HTMLDivElement | null
            if (!inner) return

            el.setAttribute('data-height', inner.offsetHeight.toString())
            if (el.style.height && el.style.height !== HEIGHT_SHORT && el.style.height !== '0px') {
                el.style.height = `${inner.offsetHeight}px`
            }
        })
    }
)

const onClickAction = (action: ToastAction) => action.onClick()

const onClose = (toast: Toast) => {
    toast.onClose?.()
    if (props.hide) props.hide(toast.id)
}

const onEnter = (el: HTMLElement, done: () => void) => {
    const wrapper = el
    const container = wrapper.parentElement as HTMLDivElement
    const toast = wrapper.querySelector('.toast__element') as HTMLDivElement
    if (!toast.getAttribute('data-height')) {
        toast.setAttribute('data-height', toast.offsetHeight.toString())
        toast.setAttribute('data-width', toast.offsetWidth.toString())
    }

    const allToast = Array.from(container.querySelectorAll('.toast__element') as NodeListOf<HTMLDivElement>)
    allToast
        .reverse()
        .slice(0, 4)
        .forEach((toastItem, idx) => {
            switch (idx) {
                case 0:
                    break
                case 1:
                    animateToast(toastItem, {
                        height: {
                            value: [toastItem.offsetHeight, HEIGHT_SHORT],
                            duration: DURATION_LONG,
                        },
                        width: {
                            value: [toastItem.offsetWidth, toastItem.offsetWidth - WIDTH_STEP],
                            duration: DURATION_LONG,
                        },
                    })
                    break
                case 2:
                    animateToast(toastItem, {
                        width: {
                            value: [toastItem.offsetWidth, toastItem.offsetWidth - WIDTH_STEP],
                            duration: DURATION_LONG,
                        },
                    })
                    break
                case 3:
                    animateToast(toastItem, {
                        opacity: {
                            value: 0,
                            duration: DURATION_LONG,
                        },
                        width: {
                            value: [toastItem.offsetWidth, toastItem.offsetWidth - WIDTH_STEP],
                            duration: DURATION_LONG,
                        },
                        height: {
                            value: [toastItem.offsetHeight, 0],
                            duration: DURATION_LONG,
                        },
                    })
                    break
            }
        })

    animateToast(toast, {
        duration: DURATION,
        opacity: [0, 1],
        height: {
            value: [0, toast!.offsetHeight],
            duration: DURATION_LONG,
        },
        complete: done,
    })
}

const onLeave = (el: HTMLElement, done: () => null) => {
    const toast = el.querySelector('.toast__element') as HTMLDivElement

    if (props.toasts.length > 2 && !isHover.value) {
        done()
        return
    }

    animateToast(toast, {
        opacity: [1, 0],
        height: [toast!.offsetHeight, 0],
        duration: DURATION,
        easing: 'easeOutQuart',
        complete: done,
    })
}

const onMouseEnter = () => {
    isHover.value = true
    if (props.pause) props.pause()

    animateToast(elements.value, {
        height: {
            value: (el: HTMLDivElement) => el.getAttribute('data-height'),
            duration: DURATION_LONG,
        },
        width: {
            value: (el: HTMLDivElement) => el.getAttribute('data-width'),
            duration: DURATION_LONG,
        },
        opacity: 1,
    })
}

const onMouseLeave = (el: MouseEvent) => {
    isHover.value = false
    if (props.resume) props.resume()

    const container = el.target as HTMLDivElement
    const allToast = Array.from(container.querySelectorAll('.toast__element') as NodeListOf<HTMLDivElement>)

    const lastToast = allToast.reverse().slice(3)
    const getWidthToRemove = (item: HTMLDivElement, idx: number) => {
        return item.clientWidth < WIDTH_STEP * (idx + 3) ? item.clientWidth : WIDTH_STEP * (idx + 3)
    }
    animateToast(lastToast, {
        opacity: [1, 0],
        width: (el: HTMLDivElement, idx: number) => [el.offsetWidth, el.clientWidth - getWidthToRemove(el, idx)],
        height: (el: HTMLDivElement) => [el.offsetHeight, 0],
        duration: (el: HTMLDivElement, idx: number) => 1000 - idx * 100,
    })

    if (allToast.length > 1) {
        const middleToast = [allToast[1]]
        if (allToast.length > 2) middleToast.push(allToast[2])
        animateToast(middleToast, {
            width: {
                value: (el: HTMLDivElement, idx: number) => [el.offsetWidth, el.clientWidth - WIDTH_STEP * (idx + 1)],
                duration: DURATION_LONG,
            },
            height: {
                value: (el: HTMLDivElement) => [el.offsetHeight, HEIGHT_SHORT],
                duration: DURATION_LONG,
            },
        })
    }
}
</script>

<template>
    <div class="toast" @mouseenter="onMouseEnter" @mouseleave="onMouseLeave">
        <TransitionGroup :css="false" appear @enter="onEnter" @leave="onLeave">
            <div v-for="toast in toasts" ref="wrappers" :key="toast.id" class="toast__wrapper">
                <div ref="elements" :class="elementClasses(toast)">
                    <div class="toast__inner">
                        <component :is="toast.icon" class="toast__icon" />
                        <div class="flex-1">
                            <p>{{ toast.label }}</p>
                            <p v-if="toast.description" class="toast__description">{{ toast.description }}</p>
                            <div v-if="toast.actions.length" class="toast__actions">
                                <button
                                    v-for="action in toast.actions"
                                    :key="action.label"
                                    type="button"
                                    :class="['btn btn--sm', { 'btn--primary': action.primary }]"
                                    @click="onClickAction(action)"
                                >
                                    {{ action.label }}
                                </button>
                            </div>
                        </div>
                        <button
                            v-if="toast.closable"
                            type="button"
                            class="toast__close"
                            aria-label="Close"
                            @click="onClose(toast)"
                        >
                            <XMarkIcon class="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>
        </TransitionGroup>
    </div>
</template>
